import type {
  Feature,
  GeoJsonProperties,
  Geometry,
  Position,
} from 'geojson'
import { isStoredCircleFeature } from './googleCircleFeature'

type LngLatCoordinate = [number, number]
type SegmentIntersection = {
  segmentA: number
  segmentB: number
  point: LngLatCoordinate
}

const DEFAULT_SIMPLIFY_TOLERANCE = 0.0001
const TERRA_DRAW_COORDINATE_PRECISION = 9

function getOuterRing(feature: Feature<Geometry, GeoJsonProperties> | null) {
  if (!feature || feature.geometry.type !== 'Polygon') return []

  return feature.geometry.coordinates[0] ?? []
}

function toLngLatCoordinate(position: Position): LngLatCoordinate | null {
  const [lng, lat] = position

  if (typeof lng !== 'number' || typeof lat !== 'number') return null

  return [lng, lat]
}

function toLngLatRing(ring: Position[]): LngLatCoordinate[] | null {
  const nextRing = ring.map(toLngLatCoordinate)

  if (nextRing.some((coordinate) => coordinate === null)) return null

  return nextRing as LngLatCoordinate[]
}

function roundCoordinateValue(value: number) {
  const factor = 10 ** TERRA_DRAW_COORDINATE_PRECISION

  return Math.round(value * factor) / factor
}

function roundLngLatCoordinate(coordinate: LngLatCoordinate): LngLatCoordinate {
  return [
    roundCoordinateValue(coordinate[0]),
    roundCoordinateValue(coordinate[1]),
  ]
}

function roundLngLatRing(coords: LngLatCoordinate[]) {
  return closeRing(coords.map(roundLngLatCoordinate))
}

function closeRing(coords: LngLatCoordinate[]) {
  if (coords.length === 0) return coords

  const first = coords[0]
  const last = coords[coords.length - 1]

  if (!first || !last || (first[0] === last[0] && first[1] === last[1])) {
    return coords
  }

  return [...coords, [first[0], first[1]] satisfies LngLatCoordinate]
}

function simplifyPolygonCoordinates(
  coords: LngLatCoordinate[],
  tolerance = DEFAULT_SIMPLIFY_TOLERANCE
): LngLatCoordinate[] {
  if (coords.length <= 3) return coords

  const simplified: LngLatCoordinate[] = [coords[0]]

  for (let index = 1; index < coords.length - 1; index += 1) {
    const previous = simplified[simplified.length - 1]
    const current = coords[index]

    if (!previous || !current) continue

    const distance = Math.hypot(
      current[0] - previous[0],
      current[1] - previous[1]
    )

    if (distance > tolerance) {
      simplified.push(current)
    }
  }

  const last = coords[coords.length - 1]

  if (last) {
    simplified.push(last)
  }

  return simplified
}

function getLineIntersection(
  p1: LngLatCoordinate,
  p2: LngLatCoordinate,
  p3: LngLatCoordinate,
  p4: LngLatCoordinate
): LngLatCoordinate | null {
  const [x1, y1] = p1
  const [x2, y2] = p2
  const [x3, y3] = p3
  const [x4, y4] = p4

  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)

  if (Math.abs(denominator) < 1e-10) return null

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator

  if (t < 0 || t > 1 || u < 0 || u > 1) return null

  return roundLngLatCoordinate([x1 + t * (x2 - x1), y1 + t * (y2 - y1)])
}

function findPolygonSelfIntersections(coords: LngLatCoordinate[]) {
  const intersections: SegmentIntersection[] = []

  for (let index = 0; index < coords.length - 1; index += 1) {
    for (
      let compareIndex = index + 2;
      compareIndex < coords.length - 1;
      compareIndex += 1
    ) {
      if (compareIndex === coords.length - 2 && index === 0) continue

      const intersection = getLineIntersection(
        coords[index],
        coords[index + 1],
        coords[compareIndex],
        coords[compareIndex + 1]
      )

      if (intersection) {
        intersections.push({
          point: intersection,
          segmentA: index,
          segmentB: compareIndex,
        })
      }
    }
  }

  return intersections
}

function hasIntersections(coords: LngLatCoordinate[]) {
  return findPolygonSelfIntersections(coords).length > 0
}

function removePolygonSelfIntersections(
  coords: LngLatCoordinate[]
): LngLatCoordinate[] {
  if (coords.length < 4) return coords

  const intersections = findPolygonSelfIntersections(coords)
  if (intersections.length === 0) return coords

  intersections.sort(
    (left, right) =>
      Math.min(left.segmentA, left.segmentB) -
      Math.min(right.segmentA, right.segmentB)
  )

  const [firstIntersection] = intersections
  if (!firstIntersection) return coords

  const startSegment = Math.min(
    firstIntersection.segmentA,
    firstIntersection.segmentB
  )
  const endSegment = Math.max(
    firstIntersection.segmentA,
    firstIntersection.segmentB
  )
  const result: LngLatCoordinate[] = []

  for (let index = 0; index <= startSegment; index += 1) {
    const coordinate = coords[index]

    if (coordinate) result.push(coordinate)
  }

  result.push(firstIntersection.point)

  for (let index = endSegment + 1; index < coords.length; index += 1) {
    const coordinate = coords[index]

    if (coordinate) result.push(coordinate)
  }

  if (result.length >= 4 && hasIntersections(result)) {
    return removePolygonSelfIntersections(result)
  }

  return result
}

function createValidPolygonCoordinates(
  coords: LngLatCoordinate[]
): LngLatCoordinate[] {
  if (coords.length < 4) return coords

  const cleaned: LngLatCoordinate[] = [coords[0]]

  for (let index = 1; index < coords.length; index += 1) {
    const previous = cleaned[cleaned.length - 1]
    const current = coords[index]

    if (!previous || !current) continue

    if (previous[0] !== current[0] || previous[1] !== current[1]) {
      cleaned.push(current)
    }
  }

  if (cleaned.length < 4) return coords

  return removePolygonSelfIntersections(cleaned)
}

export function simplifyDrawPolygonFeature<
  TProperties extends GeoJsonProperties = GeoJsonProperties,
>(
  feature: Feature<Geometry, TProperties> | null
): Feature<Geometry, TProperties> | null {
  if (!feature || isStoredCircleFeature(feature)) return feature
  if (feature.geometry.type !== 'Polygon') return feature

  const outerRing = feature.geometry.coordinates[0]
  if (!outerRing) return feature

  const coords = toLngLatRing(outerRing)
  if (!coords || coords.length < 4) return feature

  const simplified =
    coords.length > 4 ? simplifyPolygonCoordinates(coords) : coords
  const validCoords = createValidPolygonCoordinates(simplified)
  const closedCoords = roundLngLatRing(validCoords)

  if (closedCoords.length < 4) return feature

  const nextFeature = {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: [
        closedCoords,
        ...feature.geometry.coordinates.slice(1),
      ],
    },
  }

  return nextFeature
}
